import React, { useContext, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Image,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFonts } from '@use-expo/font';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-community/async-storage';
import MyMessagePopup from '../components/MyMessagePopup';
import MyCheckbox from '../components/MyCheckbox';
import { LocStr } from '../utils/Utils';
import { Context as PayKeyDashboardServerContext } from '../context/PayKeyDashboardServerContext';
import { expo } from '../../app.json';
import {
  font_Quicksand_Bold,
  font_Quicksand_Light,
  font_Quicksand_Medium,
  font_Quicksand_Regular,
  font_Quicksand_SemiBold,
  font_rubik_Bold,
  font_rubik_Medium,
  font_rubik_Regular,
} from '../utils/FontsManagementUtils';
import {
  enter_valid_password,
  enter_valid_username,
  login_str,
  ok_str,
  Password_str,
  Remember_me,
  Sorry_window_size,
  Username_str,
} from '../utils/DictionaryManagementUtils';
import { img_paykey } from '../utils/ImagesManagementUtils';

//----------------------------------------------------------------------

const window = Dimensions.get('window');
const screen = Dimensions.get('screen');

export default function MainScreen({ navigation }) {
  // ============= variables ====================

  const prefKeyLoginDetails = 'prefKeyLoginDetails';

  //----------------------------------------------------------------------

  const [language, setLanguage] = useState('english');

  const [state, setState] = useState({
    username: '',
    password: '',
    rememberMe: false,
    token: '',
    userId: '',
    userType: '',
  });

  const [animating, setAnimating] = useState(false);

  const [messagePopupState, setMessagePopupState] = useState({
    isMessageVisible: false,
    title: '',
  });

  const {
    loginUser, readMe, getAllFiles, getFontFile,
  } = useContext(
    PayKeyDashboardServerContext,
  );

  // ######################################################################

  const [dimensions, setDimensions] = useState({ window, screen });

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;

  useEffect(() => {
    Linking.getInitialURL()
      .then((url) => {
        console.log(`App.js getInitialURL Triggered: ${url}`);
        const index = url.toLowerCase().indexOf('/wls-demo/id=');
        if (index !== -1) {
          const wlsId = url.substring(index + 13);
          console.log(`WLS id=${wlsId}`);
          navigation.navigate('MainScreen');
        }

        // this.handleOpenURL({ url });
      })
      .catch((error) => console.error(error));

    loadLoginPrefs();

    Dimensions.addEventListener('change', onChange);
    return () => {
      Dimensions.removeEventListener('change', onChange);
    };
  }, []);

  const onChange = ({ window, screen }) => {
    setDimensions({ window, screen });
  };

  //----------------------------------------------------------------------

  const [fontsLoaded] = useFonts({
    'Rubik-Regular': font_rubik_Regular,
    'Rubik-Medium': font_rubik_Medium,
    'Rubik-Bold': font_rubik_Bold,
    'Quicksand-Bold': font_Quicksand_Bold,
    'Quicksand-Light': font_Quicksand_Light,
    'Quicksand-Medium': font_Quicksand_Medium,
    'Quicksand-Regular': font_Quicksand_Regular,
    'Quicksand-SemiBold': font_Quicksand_SemiBold,
  });

  //-----------------------------------------------------------------------------

  const loadLoginPrefs = async () => {
    try {
      const item = await AsyncStorage.getItem(prefKeyLoginDetails);
      if (item !== null) {
        setLanguage('english');

        const jsonObj = JSON.parse(item);
        setState({
          username: jsonObj.username,
          password: '',
          rememberMe: jsonObj.rememberMe,
          token: jsonObj.token,
          userId: jsonObj.userId,
          userType: jsonObj.userType,
        });
      }
    } catch (err) {}
  };

  // ######################################################################

  const onLoginPressed = () => {
    if (animating) return;

    if (state.username.length < 4) {
      const txt = LocStr(enter_valid_username, language);
      setMessagePopupState({ isMessageVisible: true, title: txt });
      return;
    }

    if (state.password.length < 4) {
      const txt = LocStr(enter_valid_password, language);
      setMessagePopupState({ isMessageVisible: true, title: txt });
      return;
    }

    serverLoginUser();
  };

  //----------------------------------------------------------------------

  // ######################################################################
  // ###   S e r v e r   s t u f f
  // ######################################################################

  const serverLoginUser = async () => {
    try {
      setAnimating(true);

      const obj = {
        username: state.rememberMe ? state.username : '',
        rememberMe: state.rememberMe,
        token: '',
      };

      await AsyncStorage.setItem(
        prefKeyLoginDetails,
        JSON.stringify(obj),
        () => {
          loginUser(
            state.username,
            state.password,
            (result) => {
              setAnimating(false);

              // {"_id":"5f4df4349d3d28974a34a4ea","userType":"admin","userName":"orly"}

              setState({
                username: state.username,
                password: state.password,
                rememberMe: state.rememberMe,
                token: result.token,
                userId: result.userDetails._id,
                userType: result.userDetails.userType,
              });

              serverReadMe(
                result.token,
                result.userDetails._id,
                result.userDetails.userType,
              );
            },
            (errMsg) => {
              setAnimating(false);

              console.log(errMsg);
              setMessagePopupState({ isMessageVisible: true, title: errMsg });
            },
          );
        },
      );
    } catch (err) {}
  };

  //= ============================================================================

  const serverReadMe = async (token, userId, userType) => {
    try {
      setAnimating(true);

      const obj = {
        username: state.rememberMe ? state.username : '',
        rememberMe: state.rememberMe,
        token,
        userId,
        userType,
      };

      await AsyncStorage.setItem(
        prefKeyLoginDetails,
        JSON.stringify(obj),
        () => {
          readMe(
            token,
            (result) => {
              setAnimating(false);

              // extract 'details' field:
              let userDetails = null;
              if (result.details !== undefined) userDetails = JSON.parse(result.details);

              const userItem = {
                userId,
                userUsername: state.username,
                userPassword: state.password,
                userType,
                userBlocked: result.blocked,
                userInstitute: result.clientName,
                userRole: userDetails === null ? '' : userDetails.role,
                userFullname: userDetails === null ? '' : userDetails.fullname,
                userEmail: userDetails === null ? '' : userDetails.email,
                userPhone: userDetails === null ? '' : userDetails.phone,
                userConfigs: result.configurations,
              };

              if (userType === 'admin') {
                navigation.navigate('AdminScreen', {
                  token,
                  loggedInUserId: userId,
                  userItem,
                  language,
                });
              } else {
                gotoMain(userItem, token, userId);
              }
            },
            (errMsg) => {
              setAnimating(false);

              console.log(errMsg);
              setMessagePopupState({ isMessageVisible: true, title: errMsg });
            },
          );
        },
      );
    } catch (err) {}
  };

  //= =====================================================================

  const gotoMain = (selectedUser, token, userId) => {
    navigation.navigate('MainScreen', {
      token,
      loggedInUserId: userId,
      userItem: selectedUser,
    });
  };

  // ######################################################################

  // ============= render ====================

  if (screenWidth < 900) {
    return (
      <View style={styles.container}>
        <View
          style={{
            height: screenHeight,
            width: screenWidth,
            justifyContent: 'center',
          }}
        >
          <Text style={styles.loadingTitle}>
            {LocStr(Sorry_window_size, language)}
          </Text>
        </View>
      </View>
    );
  }

  if (!fontsLoaded) {
    return <View style={styles.container} />;
  }
  return (
    <View style={styles.container}>
      <View style={styles.bodyStyle}>
        {/* -----   U s e r n a m e   ----- */}

        <View style={{ alignSelf: 'center', flexDirection: 'row' }}>
          <Text style={styles.lblTitle}>
            {LocStr(Username_str, language)}
          </Text>

          <View
            style={{
              width: 300,
              height: 27,
              marginLeft: 10,
              borderRadius: 3,
              backgroundColor: '#a9a9a9',
            }}
          >
            <TextInput
              style={{
                margin: 1,
                borderRadius: 2,
                backgroundColor: '#fff',
                borderBottomWidth: 4,
                borderTopWidth: 4,
                borderRightWidth: 4,
                borderLeftWidth: 4,
                borderColor: '#fff',
              }}
              value={state.username}
              multiline={false}
              selectTextOnFocus
              maxLength={100}
              onChangeText={(newText) => {
                // if (!newText || newText.match(/^\d{1,}(\.\d{0,2})?$/))
                {
                  setState({
                    username: newText,
                    password: state.password,
                    rememberMe: state.rememberMe,
                    token: state.token,
                    userId: state.userId,
                    userType: state.userType,
                  });
                }
              }}
              onKeyPress={(e) => {
                if (e.nativeEvent.key === 'Enter') {
                  // onValueChanged(textId,state);
                }
              }}
            />
          </View>
        </View>

        {/* -----   P a s s w o r d   ----- */}

        <View
          style={{ alignSelf: 'center', marginTop: 20, flexDirection: 'row' }}
        >
          <Text style={styles.lblTitle}>
            {LocStr(Password_str, language)}
          </Text>

          <View
            style={{
              width: 300,
              height: 27,
              marginLeft: 10,
              borderRadius: 3,
              backgroundColor: '#a9a9a9',
            }}
          >
            <TextInput
              style={{
                margin: 1,
                borderRadius: 2,
                backgroundColor: '#fff',
                borderBottomWidth: 4,
                borderTopWidth: 4,
                borderRightWidth: 4,
                borderLeftWidth: 4,
                borderColor: '#fff',
              }}
              value={state.password}
              multiline={false}
              selectTextOnFocus
              maxLength={100}
              secureTextEntry
              onChangeText={(newText) => {
                // if (!newText || newText.match(/^\d{1,}(\.\d{0,2})?$/))
                {
                  setState({
                    username: state.username,
                    password: newText,
                    rememberMe: state.rememberMe,
                    token: state.token,
                    userId: state.userId,
                    userType: state.userType,
                  });
                }
              }}
              onKeyPress={(e) => {
                if (e.nativeEvent.key === 'Enter') {
                  onLoginPressed();
                }
              }}
            />
          </View>
        </View>

        {/* -----   R e m e m b e r   m e   ----- */}

        <View
          style={{
            marginLeft: 0,
            marginTop: 20,
            width: 90 + 10 + 300,
            alignSelf: 'center',
          }}
        >
          <MyCheckbox
            style={{ marginLeft: 0, marginTop: 4, position: 'absolute' }}
            title={LocStr(Remember_me, language)}
            value={state.rememberMe}
            onValueChanged={(value) => {
              setState({
                username: state.username,
                password: state.password,
                rememberMe: value,
                token: state.token,
                userId: state.userId,
                userType: state.userType,
              });
            }}
          />

          <TouchableOpacity
            style={styles.btnSend}
            onPress={() => {
              onLoginPressed();
            }}
          >
            {animating ? (
              <ActivityIndicator
                animating={animating}
                color="#ffffff"
                size="small"
                style={styles.activityIndicator}
              />
            ) : (
              <Text style={styles.bottomLabelWhite}>
                {LocStr(login_str, language)}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* ================================   T o p   b a r   ================================ */}

      <View
        style={{
          height: 60,
          width: screenWidth,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 12,
          justifyContent: 'center',
          // alignItems: 'center',
          backgroundColor: '#fff',
          position: 'absolute',
        }}
      >
        {/* <Text style={styles.topBarTitle} >PayKeyGO</Text> */}
        <View style={styles.topBarImageAndVer}>
          <Image
            fadeDuration={0}
            style={styles.topBarImage}
            source={img_paykey}
          />
        </View>
      </View>

      {messagePopupState.isMessageVisible ? (
        <MyMessagePopup
          parentWidth={screenWidth}
          onOkPressed={() => setMessagePopupState({ isMessageVisible: false, title: '' })}
          okTitle={LocStr(ok_str, language)}
          message={messagePopupState.title}
          language={language}
        />
      ) : null}

      <Text
        style={{
          marginLeft: screenWidth - 100 - 4,
          marginTop: screenHeight - 15,
          width: 100,
          height: 11,
          position: 'absolute',
          textAlign: 'right',
          fontFamily: 'Rubik-Medium',
          fontSize: 10,
          color: '#008cff',
        }}
      >
        {expo.version}
      </Text>
    </View>
  );
}

// ============= styles ====================

MainScreen.navigationOptions = {
  headerShown: false,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#f1f5f9',
    // prevent user-select on texts
    userSelect: 'none',
  },

  loadingTitle: {
    textAlign: 'center',
    fontFamily: 'Quicksand-Medium',
    fontSize: 20,
    color: '#001a2f',
  },

  topBarTitle: {
    height: 25,
    textAlign: 'center',
    fontFamily: 'Quicksand-Medium',
    fontSize: 20,
    color: '#001a2f',
    backgroundColor: '#fff',
  },

  topBarImageAndVer: {
    marginLeft: 24,
    marginTop: 0,
    width: 136 + 30,
    height: 60,
    position: 'absolute',
  },
  topBarImage: {
    marginLeft: 0,
    marginTop: 12,
    width: 136,
    height: 36,
    resizeMode: 'contain',
    position: 'absolute',
  },

  bodyStyle: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // flexDirection: 'flex-end',
    // backgroundColor: '#f00',
  },
  lblTitle: {
    width: 90,
    marginTop: 2,
    fontFamily: 'Rubik-Regular',
    fontSize: 18,
    color: '#001a2f',
    // position: 'absolute',
  },
  btnSend: {
    height: 36,
    width: 200,
    marginLeft: 200,
    borderRadius: 7,
    justifyContent: 'center',
    backgroundColor: '#008cff',
  },
  bottomLabelWhite: {
    // height: 70,
    fontFamily: 'Rubik-Medium',
    fontSize: 18,
    color: '#fff',
    position: 'absolute',
    // marginTop: 52,
    alignSelf: 'center',
    // backgroundColor: '#f00',
  },
  activityIndicator: {
    position: 'absolute',
    alignSelf: 'center',
    height: 30,
  },
});
